import { resolveBrandColor } from '@/overlayBrand';
import { loadGoogleFont } from '@/googleFonts';
import { solveHomography } from '@/overlayWarp';
import { layerCorners } from './layerGeometry';
import { ALL_WIDGET_TYPES } from './layerTypes';

export const THUMBNAIL_WIDTH = 240;
const MAX_IMAGE_WAIT_MS = 3000;
const MAX_UPLOAD_LENGTH = 95000;

const imageCache = new Map();
const videoFrameCache = new Map();

// Resolves to the loaded image, or null when it can't be drawn (failed to load, or a cross-origin image without CORS
// headers, which would taint the canvas and make it unreadable).
function loadImage(src) {
	if(!src) return Promise.resolve(null);
	if(!imageCache.has(src)) {
		imageCache.set(src, new Promise(resolve => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = () => resolve(null);
			img.src = src;
			setTimeout(() => resolve(null), MAX_IMAGE_WAIT_MS);
		}));
	}
	return imageCache.get(src);
}

// Resolves to a video element sitting on its first frame, or null when it can't be drawn (failed to load, no CORS headers).
function loadVideoFrame(src) {
	if(!src) return Promise.resolve(null);
	if(!videoFrameCache.has(src)) {
		videoFrameCache.set(src, new Promise(resolve => {
			const video = document.createElement('video');
			video.crossOrigin = 'anonymous';
			video.muted = true;
			video.preload = 'metadata';
			video.onloadedmetadata = () => { video.currentTime = Math.min(0.1, (video.duration || 0) / 2); };
			video.onseeked = () => resolve(video);
			video.onerror = () => resolve(null);
			video.src = src;
			setTimeout(() => resolve(null), MAX_IMAGE_WAIT_MS);
		}));
	}
	return videoFrameCache.get(src);
}

function wrapLines(ctx, text, maxWidth) {
	const lines = [];
	for(const paragraph of String(text || '').split('\n')) {
		let line = '';
		for(const word of paragraph.split(' ')) {
			const candidate = line ? `${line} ${word}` : word;
			if(line && ctx.measureText(candidate).width > maxWidth) {
				lines.push(line);
				line = word;
			} else {
				line = candidate;
			}
		}
		lines.push(line);
	}
	return lines;
}

const WARP_MESH_CELLS = 8;

function drawMeshTriangle(ctx, img, s0, s1, s2, d0, d1, d2) {
	const det = (s1[0] - s0[0]) * (s2[1] - s0[1]) - (s2[0] - s0[0]) * (s1[1] - s0[1]);
	if(!det) return;
	const a = ((d1[0] - d0[0]) * (s2[1] - s0[1]) - (d2[0] - d0[0]) * (s1[1] - s0[1])) / det;
	const b = ((d1[1] - d0[1]) * (s2[1] - s0[1]) - (d2[1] - d0[1]) * (s1[1] - s0[1])) / det;
	const c = ((d2[0] - d0[0]) * (s1[0] - s0[0]) - (d1[0] - d0[0]) * (s2[0] - s0[0])) / det;
	const d = ((d2[1] - d0[1]) * (s1[0] - s0[0]) - (d1[1] - d0[1]) * (s2[0] - s0[0])) / det;
	ctx.save();
	ctx.beginPath();
	ctx.moveTo(d0[0], d0[1]);
	ctx.lineTo(d1[0], d1[1]);
	ctx.lineTo(d2[0], d2[1]);
	ctx.closePath();
	ctx.clip();
	ctx.transform(a, b, c, d, d0[0] - a * s0[0] - c * s0[1], d0[1] - b * s0[0] - d * s0[1]);
	ctx.drawImage(img, 0, 0);
	ctx.restore();
}

// Canvas 2D has no perspective transform, so a distorted layer is drawn flat and stretched over its corners as a mesh
// of small triangles, each placed exactly by the perspective mapping.
function drawWarpedLayer(ctx, layer, overlay, k, image) {
	const w = Math.max(1, Math.ceil(layer.w * k));
	const h = Math.max(1, Math.ceil(layer.h * k));
	const flat = document.createElement('canvas');
	flat.width = w;
	flat.height = h;
	drawLayer(flat.getContext('2d'), { ...layer, x: 0, y: 0, rotation: 0, props: { ...layer.props, warp: null } }, overlay, k, image);

	const quad = layerCorners(layer).map(([ x, y ]) => [ x * k, y * k ]);
	const m = solveHomography([ [ 0, 0 ], [ w, 0 ], [ w, h ], [ 0, h ] ], quad);
	if(!m) return;
	const project = ([ x, y ]) => {
		const s = m[6] * x + m[7] * y + 1;
		return [ (m[0] * x + m[1] * y + m[2]) / s, (m[3] * x + m[4] * y + m[5]) / s ];
	};
	const n = WARP_MESH_CELLS;
	for(let i = 0; i < n; i++) {
		for(let j = 0; j < n; j++) {
			const s00 = [ w * i / n, h * j / n ];
			const s10 = [ w * (i + 1) / n, h * j / n ];
			const s01 = [ w * i / n, h * (j + 1) / n ];
			const s11 = [ w * (i + 1) / n, h * (j + 1) / n ];
			const [ d00, d10, d01, d11 ] = [ s00, s10, s01, s11 ].map(project);
			drawMeshTriangle(ctx, flat, s00, s10, s01, d00, d10, d01);
			drawMeshTriangle(ctx, flat, s10, s11, s01, d10, d11, d01);
		}
	}
}

function drawLayer(ctx, layer, overlay, k, image) {
	if(layer.props?.warp) return drawWarpedLayer(ctx, layer, overlay, k, image);
	const p = layer.props || {};
	const w = layer.w * k;
	const h = layer.h * k;
	ctx.save();
	ctx.translate((layer.x + layer.w / 2) * k, (layer.y + layer.h / 2) * k);
	ctx.rotate((layer.rotation || 0) * Math.PI / 180);
	ctx.translate(-w / 2, -h / 2);
	ctx.beginPath();
	ctx.rect(0, 0, w, h);
	ctx.clip();

	if(layer.type === 'color') {
		ctx.globalAlpha = p.opacity ?? 1;
		ctx.fillStyle = resolveBrandColor(p.fill || '#888888', overlay);
		ctx.fillRect(0, 0, w, h);
	} else if((layer.type === 'image' || layer.type === 'video') && image) {
		// A crop is a fraction of the box, which (drawn stretched, same as the real output) is the same fraction of
		// the source's own pixels on each axis.
		if(p.crop) {
			const iw = image.naturalWidth || image.videoWidth || image.width;
			const ih = image.naturalHeight || image.videoHeight || image.height;
			ctx.drawImage(image, p.crop.x * iw, p.crop.y * ih, p.crop.w * iw, p.crop.h * ih, 0, 0, w, h);
		} else {
			ctx.drawImage(image, 0, 0, w, h);
		}
	} else if(layer.type === 'text') {
		const size = (p.fontSize || 32) * k;
		ctx.font = `${p.italic ? 'italic ' : ''}${p.bold ? 'bold ' : ''}${size}px ${p.fontFamily ? `"${p.fontFamily}", ` : ''}sans-serif`;
		ctx.fillStyle = resolveBrandColor(p.color || '#ffffff', overlay);
		ctx.textBaseline = 'top';
		const align = p.align || 'left';
		ctx.textAlign = align;
		const x = align === 'center' ? w / 2 : (align === 'right' ? w : 0);
		const lines = wrapLines(ctx, p.text, w);
		const free = h - lines.length * size * 1.2;
		const top = p.verticalAlign === 'middle' ? free / 2 : (p.verticalAlign === 'bottom' ? free : 0);
		lines.forEach((line, i) => ctx.fillText(line, x, top + i * size * 1.2));
	} else {
		// Widgets aren't run in the editor, and an image that couldn't be loaded has nothing to draw: a labelled box.
		ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
		ctx.fillRect(0, 0, w, h);
		ctx.fillStyle = '#ffffff';
		ctx.font = '10px sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		const label = layer.type === 'widget' ? (ALL_WIDGET_TYPES.find(t => t.value === p.widgetType)?.label || p.widgetType) : (layer.type === 'video' ? 'Video' : 'Image');
		ctx.fillText(label, w / 2, h / 2, w - 4);
	}
	ctx.restore();
}

// Draws the scene's visible layers onto a 240px-wide canvas (transparent background) and returns it as a data URL.
export async function renderSceneThumbnail(overlay, scene) {
	const width = THUMBNAIL_WIDTH;
	const height = Math.max(1, Math.round(width * overlay.height / overlay.width));
	const k = width / overlay.width;
	const layers = scene.layers.filter(l => l.visible).sort((a, b) => a.sort_order - b.sort_order);
	const images = await Promise.all(layers.map(l => l.type === 'image' ? loadImage(l.props?.src) : (l.type === 'video' ? loadVideoFrame(l.props?.src) : null)));
	await Promise.all(layers.filter(l => l.type === 'text').map(l => loadGoogleFont(l.props?.fontFamily)));

	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext('2d');
	layers.forEach((layer, i) => drawLayer(ctx, layer, overlay, k, images[i]));

	let data = canvas.toDataURL('image/webp', 0.85);
	if(!data.startsWith('data:image/webp')) data = canvas.toDataURL('image/png');
	if(data.length > MAX_UPLOAD_LENGTH) {
		// Photographic content: flatten onto black and compress harder so it still fits the upload limit.
		const flat = document.createElement('canvas');
		flat.width = width;
		flat.height = height;
		const fctx = flat.getContext('2d');
		fctx.fillStyle = '#000';
		fctx.fillRect(0, 0, width, height);
		fctx.drawImage(canvas, 0, 0);
		data = flat.toDataURL('image/jpeg', 0.7);
	}
	return data;
}
